import { useTheme } from '@mui/material/styles';
import { URLS, CONTENT, OEM, FONTS, DISPLAY_NAME, DEFAULTS } from '../../config/app-config';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AppBar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    Divider,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Slide,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { fetchPublicVersions, selectHighestVersion } from '../../services/releaseApi';
import { secureFetch } from '../../utils/secureFetch';
import {
    API_DOCUMENTATION_GROUPS,
    createApiEndpointGroups,
} from './apiDocumentationData';

const DEFAULT_LANGUAGE = DEFAULTS.language;
const DEFAULT_PRODUCT = DEFAULTS.product;
const TERMINAL_JOB_STATUSES = ['COMPLETED', 'FAILED', 'STOPPED'];
const EMPTY_PARAMETERS = [];

function trimTrailingSlash(value) {
    return String(value || '').replace(/\/+$/, '');
}

function formatDefaultValue(value) {
    if (value === undefined || value === null || value === '') return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function initialParameterState(parameters, location) {
    return Object.fromEntries(
        parameters
            .filter((parameter) => parameter.in === location)
            .map((parameter) => [parameter.name, parameter.default ?? ''])
    );
}

export function buildApiUrl(server, path, queryValues = {}, pathValues = {}) {
    const resolvedPath = Object.entries(pathValues).reduce(
        (currentPath, [name, value]) => currentPath.replace(
            `{${name}}`,
            encodeURIComponent(value || '')
        ),
        path
    );
    const query = new URLSearchParams();

    Object.entries(queryValues).forEach(([name, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
            query.set(name, String(value));
        }
    });

    const queryString = query.toString();
    return `${trimTrailingSlash(server)}${resolvedPath}${queryString ? `?${queryString}` : ''}`;
}

function buildCurl({ method, url, body, requiresAuth }) {
    const lines = [`curl -sS -X ${method} \\`, `  -H "Accept: application/json" \\`];

    if (requiresAuth) {
        lines.push('  -H "Authorization: Bearer <access-token>" \\');
    }
    if (body !== undefined) {
        lines.push('  -H "Content-Type: application/json" \\');
        lines.push(`  --data '${JSON.stringify(body)}' \\`);
    }
    lines.push(`  "${url}"`);
    return lines.join('\n');
}

function SectionHeader({ title, subtitle }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                background: theme.palette.primary.main,
                color: 'white',
                p: 3,
                mb: 3,
                boxShadow: 2,
            }}
        >
            <Typography variant="h4" sx={{ fontFamily: FONTS.body, fontWeight: 700 }}>
                {title}
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
                {subtitle}
            </Typography>
        </Box>
    );
}

function CodeBlock({ code, sx }) {
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
        } catch {
            // Clipboard access can be unavailable in embedded or insecure contexts.
        }
    };

    return (
        <Box sx={{ position: 'relative', ...sx }}>
            <Paper
                variant="outlined"
                sx={{ p: 2, bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 2 }}
            >
                <Box
                    component="pre"
                    sx={{
                        m: 0,
                        pr: 4,
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    }}
                >
                    <code>{code}</code>
                </Box>
                <Tooltip title="Copy">
                    <IconButton
                        aria-label="copy"
                        onClick={handleCopy}
                        size="small"
                        sx={{ position: 'absolute', right: 8, top: 8, color: '#e2e8f0' }}
                    >
                        <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            </Paper>
        </Box>
    );
}

function ParameterTable({ rows }) {
    if (!rows.length) return null;

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ mt: 1, minWidth: 720 }}>
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                    <TableRow>
                        {['Name', 'In', 'Type', 'Required', 'Default', 'Description'].map((heading) => (
                            <TableCell key={heading} sx={{ fontWeight: 700, color: 'white' }}>{heading}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={`${row.in}-${row.name}`}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{row.name}</TableCell>
                            <TableCell>{row.in}</TableCell>
                            <TableCell>{row.type}</TableCell>
                            <TableCell>{row.required ? 'yes' : 'no'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                                {formatDefaultValue(row.default)}
                            </TableCell>
                            <TableCell>{row.description}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}

function ResponseBadge({ status }) {
    const success = String(status).startsWith('2');
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            {success
                ? <CheckCircleIcon sx={{ color: 'success.main' }} />
                : <ErrorIcon sx={{ color: 'error.main' }} />}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Response {status}
            </Typography>
        </Stack>
    );
}

function ParameterInput({ parameter, value, onChange }) {
    if (parameter.options) {
        return (
            <FormControl sx={{ minWidth: 170 }}>
                <InputLabel id={`label-${parameter.in}-${parameter.name}`}>{parameter.name}</InputLabel>
                <Select
                    labelId={`label-${parameter.in}-${parameter.name}`}
                    label={parameter.name}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                >
                    {parameter.options.map((option) => (
                        <MenuItem key={option || 'empty'} value={option}>
                            {option || 'Auto'}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    return (
        <TextField
            label={parameter.name}
            value={value}
            required={parameter.required}
            onChange={(event) => onChange(event.target.value)}
        />
    );
}

function TryItOut({ endpoint, server, setServer }) {
    const parameters = endpoint.parameters || EMPTY_PARAMETERS;
    const queryParameters = parameters.filter((parameter) => parameter.in === 'query');
    const pathParameters = parameters.filter((parameter) => parameter.in === 'path');
    const [queryValues, setQueryValues] = useState(() => initialParameterState(parameters, 'query'));
    const [pathValues, setPathValues] = useState(() => initialParameterState(parameters, 'path'));
    const [bodyText, setBodyText] = useState(() => (
        endpoint.requestBody ? JSON.stringify(endpoint.requestBody, null, 2) : ''
    ));
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        setQueryValues(initialParameterState(parameters, 'query'));
        setPathValues(initialParameterState(parameters, 'path'));
        setBodyText(endpoint.requestBody ? JSON.stringify(endpoint.requestBody, null, 2) : '');
        setResponse(null);
        setStatus(null);
    }, [endpoint, parameters]);

    const url = useMemo(
        () => buildApiUrl(server, endpoint.path, queryValues, pathValues),
        [server, endpoint.path, queryValues, pathValues]
    );

    const parsedBody = useMemo(() => {
        if (!endpoint.requestBody) return { value: undefined, error: null };
        try {
            return { value: JSON.parse(bodyText), error: null };
        } catch (error) {
            return { value: undefined, error: error.message };
        }
    }, [bodyText, endpoint.requestBody]);

    const curl = useMemo(
        () => buildCurl({
            method: endpoint.method,
            url,
            body: parsedBody.error ? undefined : parsedBody.value,
            requiresAuth: endpoint.requiresAuth,
        }),
        [endpoint.method, endpoint.requiresAuth, parsedBody, url]
    );

    const execute = async () => {
        if (parsedBody.error) {
            setStatus('Invalid JSON');
            setResponse({ error: parsedBody.error });
            return;
        }

        setLoading(true);
        setResponse(null);
        setStatus(null);

        try {
            const options = { method: endpoint.method };
            if (parsedBody.value !== undefined) {
                options.headers = { 'Content-Type': 'application/json' };
                options.body = JSON.stringify(parsedBody.value);
            }

            const result = await secureFetch(url, options, endpoint.requiresAuth);
            setStatus(String(result.status));
            const contentType = result.headers.get('content-type') || '';

            if (contentType.includes('application/json')) {
                setResponse(await result.json());
            } else if (endpoint.responseType === 'blob' || contentType.includes('application/pdf')) {
                const blob = await result.blob();
                setResponse({
                    contentType: blob.type || contentType,
                    size: `${blob.size} bytes`,
                    message: 'Binary response received successfully.',
                });
            } else {
                setResponse(await result.text());
            }
        } catch (error) {
            setStatus('Network error');
            setResponse({ error: String(error) });
        } finally {
            setLoading(false);
        }
    };

    const renderInputs = (title, inputParameters, values, setValues) => (
        inputParameters.length > 0 && (
            <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
                    {inputParameters.map((parameter) => (
                        <ParameterInput
                            key={parameter.name}
                            parameter={parameter}
                            value={values[parameter.name]}
                            onChange={(value) => setValues((current) => ({
                                ...current,
                                [parameter.name]: value,
                            }))}
                        />
                    ))}
                </Stack>
            </Box>
        )
    );

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
            <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Try it out</Typography>
                {endpoint.requiresAuth && (
                    <Alert severity="info">This request uses your current signed-in session.</Alert>
                )}
                <TextField
                    label="Server"
                    value={server}
                    onChange={(event) => setServer(event.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: <InputAdornment position="start">🌐</InputAdornment>,
                    }}
                />

                {renderInputs('Path parameters', pathParameters, pathValues, setPathValues)}
                {renderInputs('Query parameters', queryParameters, queryValues, setQueryValues)}

                {endpoint.requestBody && (
                    <TextField
                        label="JSON request body"
                        value={bodyText}
                        onChange={(event) => setBodyText(event.target.value)}
                        error={Boolean(parsedBody.error)}
                        helperText={parsedBody.error || 'Edit the payload before executing the request.'}
                        multiline
                        minRows={7}
                        fullWidth
                        InputProps={{ sx: { fontFamily: 'monospace' } }}
                    />
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Button
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        onClick={execute}
                        disabled={loading || Boolean(parsedBody.error)}
                        sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
                    >
                        {loading ? 'Executing…' : 'Execute'}
                    </Button>
                    <TextField label="Request URL" value={url} fullWidth InputProps={{ readOnly: true }} />
                </Stack>

                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>cURL</Typography>
                    <CodeBlock code={curl} />
                </Box>

                {response !== null && (
                    <Box>
                        <Divider sx={{ my: 2 }} />
                        <ResponseBadge status={status} />
                        <CodeBlock
                            code={typeof response === 'string'
                                ? response
                                : JSON.stringify(response, null, 2)}
                        />
                    </Box>
                )}
            </Stack>
        </Paper>
    );
}

function EndpointCard({ endpoint, server, setServer, imageExampleBase }) {
    const parameters = endpoint.parameters || EMPTY_PARAMETERS;
    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, mb: 3 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Chip label={endpoint.method} size="small" color="primary" sx={{ fontWeight: 700 }} />
                    <Chip
                        label={endpoint.path}
                        size="small"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace', borderRadius: 1, fontWeight: 600 }}
                    />
                    <Chip label={endpoint.access} size="small" variant="outlined" />
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: FONTS.body }}>
                    {endpoint.title}
                </Typography>
                <Typography color="text.secondary">{endpoint.summary}</Typography>
                <Divider />

                {parameters.length > 0 && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Parameters</Typography>
                        <ParameterTable rows={parameters} />
                    </Box>
                )}

                {endpoint.requestBody && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Request body</Typography>
                        <CodeBlock code={JSON.stringify(endpoint.requestBody, null, 2)} />
                    </Box>
                )}

                {endpoint.exampleResponse && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                            Example response{endpoint.responseStatus ? ` · ${endpoint.responseStatus}` : ''}
                        </Typography>
                        <CodeBlock code={JSON.stringify(endpoint.exampleResponse, null, 2)} />
                    </Box>
                )}

                {endpoint.id === 'article' && (
                    <Alert severity="info">
                        Image URL pattern: <code>{CONTENT.imageBucketBaseUrl}/&#123;product&#125;_{OEM}_&#123;ver&#125;_&#123;lang&#125;/&#123;filename&#125;</code>
                        <br />
                        Current example: <code>{imageExampleBase}/267214.png</code>
                    </Alert>
                )}

                {endpoint.notes?.length > 0 && (
                    <Accordion disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 700 }}>Behavior and notes</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
                                {endpoint.notes.map((note) => <li key={note}>{note}</li>)}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}

                <TryItOut endpoint={endpoint} server={server} setServer={setServer} />
            </Stack>
        </Paper>
    );
}

function Overview({ server, release, versionError }) {
    const releaseReady = Boolean(release.version);
    return (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontFamily: FONTS.body, fontWeight: 800, mb: 1 }}>
                Overview
            </Typography>
            <Typography sx={{ mb: 2 }}>
                The public API exposes documentation content and asynchronous job workflows.
                Long-running operations return a job ID immediately, poll the Jobs API for completion before
                retrieving the generated result.
            </Typography>

            {versionError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The newest public version could not be loaded. Enter a version manually in a request.
                </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="overline">Base URL</Typography>
                    <Typography sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{server}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                    <Typography variant="overline">Default release</Typography>
                    <Typography sx={{ fontFamily: 'monospace' }}>
                        {releaseReady
                            ? `${release.product} / ${release.version} / ${release.language}`
                            : 'Loading newest public version…'}
                    </Typography>
                </Paper>
            </Stack>

            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Job lifecycle</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box component="ol" sx={{ mt: 0, mb: 1, pl: 3 }}>
                        <li>Start work with <code>POST /print</code>, <code>POST /llm</code>, or another job-producing route.</li>
                        <li>Read the returned <code>jobId</code> and result URL.</li>
                        <li>Poll <code>GET /jobs/&#123;job_id&#125;</code> while the job is pending or running.</li>
                        <li>Stop polling at {TERMINAL_JOB_STATUSES.map((status) => <code key={status}> {status}</code>)}.</li>
                        <li>On completion, fetch <code>pdfUrl</code> or <code>answerUrl</code> before it expires.</li>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Conventions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
                        <li>JSON is used for request and response bodies unless a route streams a PDF.</li>
                        <li>Release-aware routes require product, version, and language values.</li>
                        <li>The interactive examples initially select the numerically newest public version from <code>GET /admin/versions</code>.</li>
                        <li>Feature-gated routes can return 403 when a tenant does not enable that capability.</li>
                        <li>Generated PDF and LLM results live in a temporary cache and can later return 404.</li>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
}

function ApiDocumentation() {
    const [tab, setTab] = useState(0);
    const [server, setServer] = useState(URLS.apiBase);
    const [versions, setVersions] = useState([]);
    const [versionError, setVersionError] = useState(null);
    const [release, setRelease] = useState({
        language: DEFAULT_LANGUAGE,
        version: '',
        product: DEFAULT_PRODUCT,
    });

    useEffect(() => {
        let cancelled = false;

        fetchPublicVersions()
            .then((publicVersions) => {
                if (cancelled) return;
                const newest = selectHighestVersion(publicVersions);
                if (!newest) throw new Error('No public documentation versions are available.');

                const products = newest.products || [];
                const languages = newest.languages || [];
                setVersions(publicVersions);
                setRelease({
                    version: newest.code,
                    product: products.includes(DEFAULT_PRODUCT) ? DEFAULT_PRODUCT : (products[0] || ''),
                    language: languages.includes(DEFAULT_LANGUAGE) ? DEFAULT_LANGUAGE : (languages[0] || ''),
                });
                setVersionError(null);
            })
            .catch((error) => {
                if (!cancelled) setVersionError(error);
            });

        return () => { cancelled = true; };
    }, []);

    const versionOptions = useMemo(() => versions.map((version) => version.code), [versions]);
    const endpointGroups = useMemo(
        () => createApiEndpointGroups({ release, versionOptions }),
        [release, versionOptions]
    );
    const imageExampleBase = `${CONTENT.imageBucketBaseUrl}/${release.product}_${OEM}_${release.version}_${release.language}`;
    const activeGroup = API_DOCUMENTATION_GROUPS[tab - 1];

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
            <SectionHeader
                title={`${DISPLAY_NAME} Public API — Documentation`}
                subtitle="Content, PDF generation, LLM processing, and asynchronous job reference"
            />

            <Paper variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700 }}>Server</Typography>
                    <TextField
                        value={server}
                        onChange={(event) => setServer(event.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start">🌐</InputAdornment>,
                        }}
                    />
                </Stack>
                <Divider />
                <Tabs
                    value={tab}
                    onChange={(_event, value) => setTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    <Tab label="Overview" />
                    {API_DOCUMENTATION_GROUPS.map((group) => (
                        <Tab key={group.id} label={group.label} />
                    ))}
                </Tabs>
            </Paper>

            {tab === 0 ? (
                <Overview server={server} release={release} versionError={versionError} />
            ) : (
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                        {activeGroup.label}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {activeGroup.description}
                    </Typography>
                    {endpointGroups[activeGroup.id].map((endpoint) => (
                        <EndpointCard
                            key={endpoint.id}
                            endpoint={endpoint}
                            server={server}
                            setServer={setServer}
                            imageExampleBase={imageExampleBase}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export function ApiDocumentationDialog({ open, onClose }) {
    const theme = useTheme();
    return (
        <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
            <AppBar sx={{ position: 'sticky', bgcolor: theme.palette.primary.main }}>
                <Toolbar>
                    <Typography sx={{ flex: 1, fontFamily: FONTS.body }} variant="h6">
                        Help Public API
                    </Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    <ApiDocumentation />
                </Box>
            </DialogContent>
        </Dialog>
    );
}
